/**
 * AI Assistant personas (Phase 1).
 *
 * A persona is a selectable "mode" for the member assistant: an emoji icon +
 * a system-prompt instruction block appended LAST in the prompt's stable
 * prefix. Off-platform role-play personas (Nurse, Mechanic, Tutor, Translator)
 * deliberately have `allowAccountTools: false` so they cannot read the
 * member's account data; the General assistant and the Business Coach (which
 * grounds coaching in the member's real standing) can. Tenants override/extend
 * the defaults via `features.assistant.personas[]` (merged by id; editor UI is
 * Phase 2).
 *
 * Personas can also declare `intake` questions — a small member profile the
 * persona asks for up front (the UI shows a card on the first chat; the
 * persona also asks naturally in-conversation) — and `suggestedQuestions`
 * starter chips, optionally templated on the intake answers.
 *
 * Avatar images are Phase 2 — `icon` is emoji-only by design.
 */

/**
 * One persona intake question. Answers are stored as `assistant_memory` rows
 * under `key` — the SAME store the `save_memory` tool writes — so an answer
 * given in the intake card and a fact shared mid-conversation converge, both
 * flow into the prompt seed, and "forget me" clears them together.
 */
export type AssistantIntakeQuestion = {
  /** snake_case memory key the answer is stored under (unique per member). */
  key: string;
  /** The question shown to the member (and asked by the persona in-chat). */
  question: string;
  /** Optional input placeholder/example for the intake card. */
  placeholder?: string;
};

export type AssistantPersona = {
  /** Stable id — override merging and conversation rows key on it. */
  id: string;
  name: string;
  /** Emoji only (no image avatars in Phase 1). */
  icon: string;
  /** One-line card subtitle shown in the persona picker. */
  tagline: string;
  /**
   * Persona instruction block, appended last in the system prompt's stable
   * prefix. Global guardrails (no earnings predictions, read-only, etc.) are
   * NOT repeated here — the assembler owns those.
   */
  instructions: string;
  /** May call the member-account tools (wallet, team, orders, rank…). */
  allowAccountTools: boolean;
  /** May use server-side web search (still gated by the tenant toggle). */
  allowWebSearch: boolean;
  /** Best-effort browser speech-synthesis voice hint (e.g. 'en-US'). */
  voiceHint?: string;
  /**
   * Member-profile questions this persona asks up front (Nurse → conditions,
   * Mechanic → vehicle…). Empty/omitted = persona has no intake.
   */
  intake?: AssistantIntakeQuestion[];
  /**
   * Starter-question chips shown on an empty thread. `{key}` placeholders
   * substitute the member's intake answer for that key; a chip whose
   * placeholder is unanswered is hidden (see `fillSuggestedQuestion`).
   */
  suggestedQuestions?: string[];
  /**
   * Tenant switched this persona off. It disappears from the picker and can't
   * start new threads; EXISTING threads on it keep working (a member mid-chat
   * shouldn't lose their conversation because an admin tidied the list).
   * Undefined means enabled — absence is the default for every built-in.
   */
  enabled?: boolean;
};

/** Tenant override: same shape, everything optional except the id. */
export type AssistantPersonaOverride = Partial<AssistantPersona> & { id: string };

/** A persona the member may start a new chat with. */
export function personaEnabled(p: AssistantPersona): boolean {
  return p.enabled !== false;
}

export const GENERAL_PERSONA_ID = 'general';

export const DEFAULT_PERSONAS: AssistantPersona[] = [
  {
    id: GENERAL_PERSONA_ID,
    name: 'Assistant',
    icon: '✨',
    tagline: 'Your all-around helper — account, platform, and general questions',
    instructions:
      'You are the member’s general-purpose personal assistant. Answer questions about ' +
      'their account and earnings using your tools, guide them around the platform, and ' +
      'help with everyday questions, writing, and translations like a capable human ' +
      'assistant would.',
    allowAccountTools: true,
    allowWebSearch: true,
    suggestedQuestions: [
      'How much have I earned this month?',
      'What do I still need for my next rank?',
      'How do I cash out my earnings?',
      'How is my team doing?',
    ],
  },
  {
    id: 'nurse',
    name: 'Nurse',
    icon: '🩺',
    tagline: 'Everyday health and wellness guidance',
    instructions:
      'You are a friendly, knowledgeable nurse. Give practical everyday health and ' +
      'wellness guidance in plain language. You are not a doctor and never diagnose or ' +
      'prescribe — for anything serious, urgent, or medication-related, tell the member ' +
      'to see a licensed medical professional.',
    allowAccountTools: false,
    allowWebSearch: true,
    intake: [
      {
        key: 'health_conditions',
        question: 'Any ongoing health conditions I should keep in mind?',
        placeholder: 'e.g. high blood pressure, diabetes — or "none"',
      },
      {
        key: 'health_meds_allergies',
        question: 'Any regular medications or allergies?',
        placeholder: 'e.g. losartan daily; allergic to penicillin',
      },
    ],
    suggestedQuestions: [
      'What should I watch out for day to day with {health_conditions}?',
      'What foods are good for someone like me?',
      'How can I sleep better?',
    ],
  },
  {
    id: 'mechanic',
    name: 'Mechanic',
    icon: '🔧',
    tagline: 'Vehicle care and troubleshooting help',
    instructions:
      'You are an experienced motor vehicle mechanic. Help the member troubleshoot car ' +
      'and motorcycle problems step by step, explain likely causes and rough repair ' +
      'options, and flag anything that is unsafe to drive with or needs a licensed shop.',
    allowAccountTools: false,
    allowWebSearch: true,
    intake: [
      {
        key: 'vehicle',
        question: 'What do you drive? (make, model, year)',
        placeholder: 'e.g. Toyota Vios 2018, Honda Click 125',
      },
      {
        key: 'vehicle_concerns',
        question: 'Anything currently bothering you about it?',
        placeholder: 'e.g. squealing when braking — or "nothing right now"',
      },
    ],
    suggestedQuestions: [
      'What regular maintenance does my {vehicle} need?',
      'Help me figure out this problem: {vehicle_concerns}',
      'What should I check before a long drive?',
    ],
  },
  {
    id: 'business-coach',
    name: 'Business Coach',
    icon: '📈',
    tagline: 'Selling skills and network-building coaching',
    instructions:
      'You are a supportive direct-selling business coach. Coach the member on selling ' +
      'skills, customer service, follow-ups, and growing their network ethically. Use ' +
      'your account tools to ground advice in their REAL standing (rank progress, team, ' +
      'actual earnings) instead of generic tips. Focus on effort and skills — never ' +
      'promise, project, or imply any income outcome.',
    allowAccountTools: true,
    allowWebSearch: true,
    intake: [
      {
        key: 'business_goal',
        question: 'What is your main goal in the business right now?',
        placeholder: 'e.g. reach Silver rank, 5 new customers this month',
      },
      {
        key: 'business_experience',
        question: 'How long have you been in direct selling, and what do you sell?',
        placeholder: 'e.g. 6 months, wellness products',
      },
    ],
    suggestedQuestions: [
      'What should I do this week to move toward: {business_goal}?',
      'How do I follow up with a prospect without being pushy?',
      'Give me a simple daily routine for growing my business.',
    ],
  },
  {
    id: 'tutor',
    name: 'Tutor',
    icon: '📚',
    tagline: 'Patient explanations for any subject',
    instructions:
      'You are a patient tutor. Explain concepts simply, check understanding with small ' +
      'questions, and adapt to the member’s level. Prefer worked examples over ' +
      'lectures.',
    allowAccountTools: false,
    allowWebSearch: true,
    intake: [
      {
        key: 'study_focus',
        question: 'Who is studying, and what subject or level?',
        placeholder: 'e.g. my daughter, Grade 6 math; me, basic English',
      },
    ],
    suggestedQuestions: [
      'Make a simple study plan for {study_focus}.',
      'Explain a topic I find confusing.',
      'Quiz me on what I have been studying.',
    ],
  },
  {
    id: 'translator',
    name: 'Translator',
    icon: '🌐',
    tagline: 'Translate between languages, naturally',
    instructions:
      'You are a translator. Translate whatever the member sends into the language they ' +
      'ask for (default: between English and Filipino), preserving tone and meaning. ' +
      'When a phrase is idiomatic, give the natural equivalent plus a literal gloss.',
    allowAccountTools: false,
    allowWebSearch: false,
    suggestedQuestions: [
      'Translate a message to English for me.',
      'How do I say this politely in Filipino?',
    ],
  },
];

/**
 * Merge tenant persona overrides over the defaults by id. Overrides with a
 * known id patch that persona; overrides with a new id append a new persona
 * (missing fields fall back to safe values — account tools OFF).
 */
export function resolvePersonas(
  overrides?: AssistantPersonaOverride[] | null,
): AssistantPersona[] {
  if (!overrides?.length) return DEFAULT_PERSONAS;
  const out = DEFAULT_PERSONAS.map((p) => ({ ...p }));
  for (const o of overrides) {
    if (!o?.id) continue;
    const existing = out.find((p) => p.id === o.id);
    if (existing) {
      Object.assign(existing, stripUndefined(o));
    } else {
      out.push({
        id: o.id,
        name: o.name ?? o.id,
        icon: o.icon ?? '🤖',
        tagline: o.tagline ?? '',
        instructions: o.instructions ?? '',
        allowAccountTools: o.allowAccountTools ?? false,
        allowWebSearch: o.allowWebSearch ?? false,
        ...(o.voiceHint ? { voiceHint: o.voiceHint } : {}),
        ...(o.intake ? { intake: o.intake } : {}),
        ...(o.suggestedQuestions ? { suggestedQuestions: o.suggestedQuestions } : {}),
        ...(o.enabled === false ? { enabled: false } : {}),
      });
    }
  }
  return out;
}

function stripUndefined<T extends object>(obj: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v;
  }
  return out as Partial<T>;
}

// ── Intake / memory-key helpers ─────────────────────────────────────────────

export const ASSISTANT_MEMORY_KEY_MAX = 64;
export const ASSISTANT_MEMORY_VALUE_MAX = 500;

/**
 * Canonical memory-key cleaning — shared by the `save_memory` tool and the
 * intake route so both paths land on the SAME `assistant_memory` row for a
 * given fact. Returns '' when nothing survives.
 */
export function normalizeAssistantMemoryKey(key: string): string {
  return key
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, ASSISTANT_MEMORY_KEY_MAX);
}

/**
 * "Don't ask again" marker rows: `intake_skipped_<persona>` in
 * `assistant_memory`. Filtered out of the prompt seed and the personas DTO —
 * they are UI state, not knowledge about the member.
 */
export const ASSISTANT_INTAKE_SKIP_PREFIX = 'intake_skipped_';

export function assistantIntakeSkipKey(personaId: string): string {
  return normalizeAssistantMemoryKey(`${ASSISTANT_INTAKE_SKIP_PREFIX}${personaId}`);
}

/**
 * A persona's intake list with tenant-override garbage dropped: entries must
 * be objects with a non-empty normalizable key and a question; keys are
 * normalized so every consumer (route, prompt, UI) agrees on the stored form.
 */
export function personaIntake(persona: AssistantPersona): AssistantIntakeQuestion[] {
  if (!Array.isArray(persona.intake)) return [];
  const out: AssistantIntakeQuestion[] = [];
  const seen = new Set<string>();
  for (const q of persona.intake) {
    if (!q || typeof q !== 'object') continue;
    const key = normalizeAssistantMemoryKey(String(q.key ?? ''));
    const question = typeof q.question === 'string' ? q.question.trim() : '';
    if (!key || !question || seen.has(key) || key.startsWith(ASSISTANT_INTAKE_SKIP_PREFIX)) continue;
    seen.add(key);
    out.push({
      key,
      question,
      ...(typeof q.placeholder === 'string' && q.placeholder ? { placeholder: q.placeholder } : {}),
    });
  }
  return out;
}

/** Every intake key declared by ANY persona (for seed partitioning). */
export function allPersonaIntakeKeys(personas: AssistantPersona[]): Set<string> {
  const keys = new Set<string>();
  for (const p of personas) for (const q of personaIntake(p)) keys.add(q.key);
  return keys;
}

/**
 * Fill a suggested-question template's `{key}` placeholders from the member's
 * intake answers. Returns null when any referenced key is unanswered (the chip
 * is hidden until the member has shared that fact). Long answers are clipped
 * so a chip stays chip-sized.
 */
export function fillSuggestedQuestion(
  template: string,
  answers: Record<string, string>,
): string | null {
  let missing = false;
  const filled = template.replace(/\{([a-z0-9_]+)\}/g, (_m, key: string) => {
    const answer = answers[key]?.trim();
    if (!answer) {
      missing = true;
      return '';
    }
    return answer.length > 60 ? `${answer.slice(0, 57)}…` : answer;
  });
  return missing ? null : filled;
}
